import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import { Button, Card, Input, Select } from '../../components/ui/Kit';
import Spinner from '../../components/ui/Spinner';
import SafeIcon from '../../common/SafeIcon';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/api';

const {
  FiArrowLeft, FiChevronDown, FiChevronUp, FiRefreshCw,
} = FiIcons;

const CATEGORY_OPTIONS = [
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'appointment', label: 'Appointment' },
  { value: 'invite', label: 'Invite' },
  { value: 'auth_recovery', label: 'Auth recovery' },
  { value: 'auth_otp', label: 'Auth OTP' },
  { value: 'sequence', label: 'Sequence' },
  { value: 'campaign', label: 'Campaign' },
  { value: 'recruit', label: 'Recruit' },
  { value: 'client', label: 'Client' },
];

const MERGE_TAG_GROUPS = [
  {
    label: 'Lead',
    tags: ['{{lead_first_name}}', '{{lead_last_name}}', '{{lead_phone}}', '{{lead_email}}'],
  },
  {
    label: 'Agent',
    tags: ['{{agent_name}}', '{{agent_first_name}}', '{{agent_email}}', '{{agent_initials}}'],
  },
  {
    label: 'Appointment',
    tags: [
      '{{appointment_date}}',
      '{{appointment_time}}',
      '{{appointment_type}}',
      '{{zoom_join_url}}',
      '{{meeting_address}}',
      '{{meeting_url}}',
    ],
  },
  {
    label: 'Auth',
    tags: ['{{verify_url}}', '{{token}}'],
  },
  {
    label: 'System',
    tags: ['{{unsubscribe_url}}', '{{agency_name}}'],
  },
];

const SAMPLE_MERGE_DATA = {
  lead_first_name: 'Jane',
  lead_last_name: 'Smith',
  lead_phone: '(555) 123-4567',
  lead_email: 'jane@example.com',
  agent_name: 'John Agent',
  agent_first_name: 'John',
  agent_email: 'john@agency.com',
  agent_initials: 'JA',
  appointment_date: 'Monday, June 9, 2026',
  appointment_time: '2:00 PM ET',
  appointment_type: 'Strategy Session',
  zoom_join_url: 'https://zoom.us/j/example',
  meeting_address: '123 Main Street, Miami, FL',
  meeting_url: 'https://example.com/meeting',
  verify_url: 'https://ogxgxzvxbmnszfxbiuai.supabase.co/auth/v1/verify?token=sample&type=recovery&redirect_to=https%3A%2F%2Fcrm.prosperityleaders.net%2Freset-password',
  token: '305805',
  unsubscribe_url: 'https://crm.prosperityleaders.net/unsubscribe?lead=sample',
  agency_name: 'Prosperity Leaders',
};

function applyMergeTags(template, fields) {
  let out = template;
  for (const [key, value] of Object.entries(fields)) {
    out = out.split(`{{${key}}}`).join(value);
  }
  return out;
}

function EmailPreview({ html }) {
  return (
    <iframe
      srcDoc={html || '<p style="padding:20px;color:#999;font-family:sans-serif;">No HTML content yet.</p>'}
      sandbox="allow-same-origin"
      style={{ width: '100%', height: '600px', border: 'none', borderRadius: '8px' }}
      title="Email Preview"
    />
  );
}

const emptyForm = () => ({
  name: '',
  category: 'campaign',
  subject: '',
  description: '',
  html_body: '',
});

export default function TemplateEditor() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();

  const [form, setForm] = useState(emptyForm);
  const [loadedTemplate, setLoadedTemplate] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [copiedTag, setCopiedTag] = useState(null);

  const isSystemTemplate = !isNew && loadedTemplate?.is_system === true;
  const canEditSystemTemplate = ['admin', 'super_admin'].includes(user?.role);
  const shouldCreateCopy = isSystemTemplate && !canEditSystemTemplate;

  useEffect(() => {
    if (isNew) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const row = await api.emailTemplates.getById(id);
        if (cancelled) return;
        setLoadedTemplate(row);
        setForm({
          name: row.name || '',
          category: row.category || 'campaign',
          subject: row.subject || '',
          description: row.description || '',
          html_body: row.html_body || '',
        });
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Could not load template',
          message: err?.message || 'Try again.',
        });
        navigate('/campaigns/templates');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, isNew, navigate, addToast]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPreviewHtml(applyMergeTags(form.html_body, SAMPLE_MERGE_DATA));
    }, 500);
    return () => clearTimeout(timer);
  }, [form.html_body]);

  const charCount = form.html_body.length;

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const copyTag = useCallback(async (tag) => {
    try {
      await navigator.clipboard.writeText(tag);
      setCopiedTag(tag);
      addToast({ type: 'success', title: 'Copied!', message: tag });
      setTimeout(() => setCopiedTag(null), 1500);
    } catch {
      addToast({ type: 'error', title: 'Copy failed', message: 'Could not copy to clipboard.' });
    }
  }, [addToast]);

  const refreshPreview = () => {
    setPreviewHtml(applyMergeTags(form.html_body, SAMPLE_MERGE_DATA));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      addToast({ type: 'error', title: 'Name is required' });
      return;
    }
    if (!user?.agency_id || !user?.id) {
      addToast({ type: 'error', title: 'Not signed in' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        subject: form.subject.trim() || null,
        description: form.description.trim() || null,
        html_body: form.html_body,
      };

      if (isNew || shouldCreateCopy) {
        await api.emailTemplates.create({
          ...payload,
          agency_id: user.agency_id,
          created_by: user.id,
          is_system: false,
        });
        addToast({
          type: 'success',
          title: shouldCreateCopy ? 'Custom copy created' : 'Template created',
        });
      } else {
        await api.emailTemplates.update(id, payload);
        addToast({ type: 'success', title: 'Template saved' });
      }
      navigate('/campaigns/templates');
    } catch (err) {
      addToast({ type: 'error', title: 'Save failed', message: err?.message || 'Try again.' });
    } finally {
      setSaving(false);
    }
  };

  const pageTitle = useMemo(() => {
    if (isNew) return 'New Template';
    if (isSystemTemplate) return 'Edit System Template';
    return 'Edit Template';
  }, [isNew, isSystemTemplate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 gap-3">
        <Spinner size="lg" className="text-primary" />
        <p className="text-sm text-gray-500">Loading template…</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-[1400px]">
      <div className="flex items-center gap-3">
        <Link
          to="/campaigns/templates"
          className="inline-flex items-center text-sm text-gray-600 hover:text-primary"
        >
          <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-1" />
          Back to templates
        </Link>
      </div>

      <h1 className="text-2xl font-bold text-gray-900">{pageTitle}</h1>

      <form onSubmit={handleSave}>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <Card className="p-4 md:p-6 space-y-4">
            <Input
              label="Name"
              required
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="Template name"
            />
            <Select
              label="Category"
              value={form.category}
              onChange={(e) => updateField('category', e.target.value)}
              options={CATEGORY_OPTIONS}
            />
            <Input
              label="Subject"
              value={form.subject}
              onChange={(e) => updateField('subject', e.target.value)}
              placeholder="Email subject line"
            />
            <div className="w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                rows={2}
                value={form.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Optional notes about this template"
                className="flex w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">HTML Body</label>
              <textarea
                value={form.html_body}
                onChange={(e) => updateField('html_body', e.target.value)}
                spellCheck={false}
                className="w-full min-h-[500px] rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                style={{ fontFamily: "'Courier New', Courier, monospace" }}
              />
              <p className="mt-1 text-xs text-gray-400">{charCount.toLocaleString()} characters</p>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setTagsOpen((o) => !o)}
                className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100"
              >
                Available Merge Tags
                <SafeIcon icon={tagsOpen ? FiChevronUp : FiChevronDown} className="w-4 h-4" />
              </button>
              {tagsOpen && (
                <div className="p-4 space-y-4 bg-white">
                  {MERGE_TAG_GROUPS.map((group) => (
                    <div key={group.label}>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                        {group.label}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {group.tags.map((tag) => (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => copyTag(tag)}
                            className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                              copiedTag === tag
                                ? 'border-green-300 bg-green-50 text-green-800'
                                : 'border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            {copiedTag === tag ? 'Copied!' : tag}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button type="submit" isLoading={saving}>
                {shouldCreateCopy ? 'Save as Custom Copy' : 'Save Template'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/campaigns/templates')}>
                Cancel
              </Button>
            </div>
          </Card>

          <Card className="p-4 md:p-6 flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-gray-700">Live Preview</label>
              <button
                type="button"
                onClick={refreshPreview}
                className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-primary"
              >
                <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5" />
                Refresh
              </button>
            </div>
            <div className="flex-1 bg-gray-50 rounded-lg overflow-hidden">
              <EmailPreview html={previewHtml} />
            </div>
            <p className="mt-3 text-xs text-gray-400">
              Preview uses sample data for merge tags
            </p>
          </Card>
        </div>
      </form>
    </div>
  );
}
